import { create } from 'zustand';
import { notificationService } from '@/services/notificationService';

// Helper function to get the active tab from localStorage
const getInitialActiveTab = () => {
    const savedTab = localStorage.getItem('activeTab');
    return savedTab ? savedTab : 'overview';
};

/** Sidebar bell red dot — use with staticBadge override in NotificationBell */
export const selectShowNotificationBellBadge = (state) =>
    Boolean(state.hasUnreadNotifications && !state.contactRequestChatDialogOpen);

const useGlobalStore = create((set, get) => ({
    // State
    showDataLossWarning: false,
    activeTab: getInitialActiveTab(), // Initialize activeTab from localStorage
    /** Authoritative for bell dot (Sidebar API, refresh after chat, user actions). */
    hasUnreadNotifications: false,
    /** Live chat modal open — hides bell dot; realtime bumps use signalUnreadFromRealtime */
    contactRequestChatDialogOpen: false,
    /** Which contact_request thread is open in chat (null if closed) — per-thread Pusher suppression */
    activeContactRequestChatId: null,

    // Actions
    toggleDataLossWarning: () => {
        set((state) => ({
            showDataLossWarning: !state.showDataLossWarning,
        }));
    },
    setDataLossWarning: (value) => {
        set({ showDataLossWarning: value });
    },
    setActiveTab: (tab) => {
        localStorage.setItem('activeTab', tab); // Save active tab to localStorage
        set({ activeTab: tab });
    },
    setHasUnreadNotifications: (value) => {
        set({ hasUnreadNotifications: !!value });
    },
    /**
     * @param {boolean} open
     * @param {string|number|null} [contactRequestId] — pass id when open=true so candidate Pusher can scope suppression
     */
    setContactRequestChatDialogOpen: (open, contactRequestId = null) => {
        const isOpen = !!open;
        let id = null;
        if (isOpen && contactRequestId != null && contactRequestId !== '') {
            const n = Number(contactRequestId);
            id = Number.isNaN(n) ? null : n;
        }
        set({
            contactRequestChatDialogOpen: isOpen,
            activeContactRequestChatId: isOpen ? id : null,
        });
    },
    /** Pusher generic notifications — do not mark unread while user is inside live chat */
    signalUnreadFromRealtime: () => {
        if (get().contactRequestChatDialogOpen) return;
        set({ hasUnreadNotifications: true });
    },
    /** Re-fetch /notifications and align bell (e.g. after closing chat / messages/read on server). */
    refreshNotificationBellFromServer: async () => {
        try {
            const data = await notificationService.getNotifications({ per_page: 100 });
            const list = Array.isArray(data) ? data : data.data || [];
            const hasUnread = list.some(
                (n) => n.status === 'unread' || n.read === false,
            );
            set({ hasUnreadNotifications: hasUnread });
        } catch (error) {
            console.warn('refreshNotificationBellFromServer:', error);
        }
    },
}));

export default useGlobalStore;